import { useLayoutEffect, useRef } from "react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { LiveTurn } from "../../../lib/aiLive";

/** Within this many pixels of the bottom counts as following along. */
const STICK_PX = 40;

/**
 * The AI call as a conversation: the AI on the right (our side, the way the
 * operator's own call panel draws the operator), the callee on the left.
 *
 * Follows the newest line only while the reader is already at the bottom, the
 * rule `pilot/TranscriptList.tsx` explains: somebody re-reading what the callee
 * said a minute ago is not pulled away by the next line.
 */
export function AiCallTranscript({ turns }: { turns: LiveTurn[] }) {
  const t = useT();
  const box = useRef<HTMLOListElement | null>(null);
  const stuck = useRef(true);

  useLayoutEffect(() => {
    const el = box.current;
    if (el && stuck.current) el.scrollTop = el.scrollHeight;
  }, [turns.length]);

  if (turns.length === 0) {
    return <p className="crm-ailive-quiet">{t("aiCalls.live.transcriptEmpty")}</p>;
  }
  return (
    <ol
      ref={box}
      className="crm-ailive-log"
      aria-live="polite"
      aria-label={t("aiCalls.live.transcript")}
      onScroll={() => {
        const el = box.current;
        if (el) stuck.current = el.scrollHeight - el.scrollTop - el.clientHeight <= STICK_PX;
      }}
    >
      {turns.map((turn) => (
        <li key={turn.seq} className={cn("crm-cp-turn", turn.role === "ai" && "is-me")}>
          <span className="crm-cp-who">{t(turn.role === "ai" ? "aiCalls.live.ai" : "aiCalls.live.callee")}</span>
          <p className="crm-cp-said">{turn.text}</p>
        </li>
      ))}
    </ol>
  );
}
